// tabs/DateTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Group, Text, Button, Divider, ActionIcon, Badge,
  Drawer, Stack, Switch, Select, Tooltip
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconPlus, IconTrash, IconPencil, IconRefresh } from '@tabler/icons-react';
import {
  useACCourseService, useLocationService,
  CourseDateModelView, CreateCourseDate, UpdateCourseDateRequest,
} from '@actschurch/ccms-sdk';
import { Table } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface DateTabProps {
  courseId: number;
  active: boolean;
  onCourseChange?: () => void;
}

// ─── Frequency options ────────────────────────────────────────────────────────
const FREQUENCY_OPTIONS = [
  { value: 'Weekly',      label: 'Weekly'      },
  { value: 'Fortnightly', label: 'Fortnightly' },
  { value: 'Monthly',     label: 'Monthly'     },
  { value: 'Daily',       label: 'Daily'       },
  { value: 'Once',        label: 'Once'        },
];

// ─── Timestamp helpers (yyyyMMddHHmmss ↔ Date) ────────────────────────────────
const dateToTimestamp = (d: Date | null): number => {
  if (!d) return 0;
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  return Number(
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
};

const timestampToDate = (ts?: number | null): Date | null => {
  if (!ts) return null;
  const s = String(ts).padEnd(14, '0');
  return new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14))
  );
};

// ─── Empty form shape ─────────────────────────────────────────────────────────
const emptyForm = (courseId: number): CreateCourseDate => ({
  courseID:        courseId,
  locationID:      0,
  frequency:       null,
  closingDateTime: 0,
  startDateTime:   0,
  endDateTime:     0,
  isRemote:        false,
});

// ─── DateTab ──────────────────────────────────────────────────────────────────
export default function DateTab({ courseId, active, onCourseChange }: DateTabProps) {
  const courseService   = useACCourseService();
  const locationService = useLocationService();
  const [dates, setDates]         = useState<CourseDateModelView[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);

  // Add/Edit drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [form, setForm]               = useState<CreateCourseDate>(emptyForm(courseId));
  const [startDate,   setStartDate]   = useState<Date | null>(null);
  const [endDate,     setEndDate]     = useState<Date | null>(null);
  const [closingDate, setClosingDate] = useState<Date | null>(null);
  const [saving, setSaving]           = useState(false);
  const [editingDate, setEditingDate] = useState<CourseDateModelView | null>(null);

  // Confirm-remove modal state
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  const fetchDates = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const result = await courseService.getDates(courseId);
      setDates(result);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load intake dates.');
    } finally {
      setLoading(false);
    }
  }, [courseId, courseService]);

  useEffect(() => {
    locationService.getAll().then(setLocations).catch(console.error);
  }, [locationService]);


  useEffect(() => {
    if (active) fetchDates();
  }, [active, fetchDates]);

  // ── Open drawer in "add" mode ─────────────────────────────────────────────
  const openDrawer = () => {
    setEditingDate(null);
    setForm(emptyForm(courseId));
    setStartDate(null);
    setEndDate(null);
    setClosingDate(null);
    setDrawerOpen(true);
  };

  // ── NEW: Open drawer in "edit" mode, pre-filled from the row ─────────────
  const openEditDrawer = (d: CourseDateModelView) => {
    setEditingDate(d);
    setForm({
      courseID:        courseId,
      locationID:      d.locationID ?? 0,
      frequency:       d.frequency ?? null,
      closingDateTime: d.closingDateTime ?? 0,
      startDateTime:   d.startDateTime ?? 0,
      endDateTime:     d.endDateTime ?? 0,
      isRemote:        d.isRemote ?? false,
    });
    setStartDate(timestampToDate(d.startDateTime));
    setEndDate(timestampToDate(d.endDateTime));
    setClosingDate(timestampToDate(d.closingDateTime));
    setDrawerOpen(true);
  };

  // ── Add or update intake date ─────────────────────────────────────────────
  const handleSaveDate = async () => {
    // Basic validation
    if (!form.locationID && !form.isRemote) {
      toast.error('Validation', 'Please select a location or mark as remote.');
      return;
    }
    if (!startDate || !endDate || !closingDate) {
      toast.error('Validation', 'Start, end, and closing dates are required.');
      return;
    }
    if (endDate <= startDate) {
      toast.error('Validation', 'End date must be after start date.');
      return;
    }

    setSaving(true);
    try {
      if (editingDate) {
        // UPDATE existing date
        const payload: UpdateCourseDateRequest = {
          locationID:      form.isRemote ? null : form.locationID,
          frequency:       form.frequency,
          startDateTime:   dateToTimestamp(startDate),
          endDateTime:     dateToTimestamp(endDate),
          closingDateTime: dateToTimestamp(closingDate),
          isRemote:        form.isRemote,
        };
        await courseService.updateDate(courseId, editingDate.dateID, payload);
        toast.success('Date Updated', 'Intake date updated.');
      } else {
        // CREATE new date
        const payload: CreateCourseDate = {
          ...form,
          startDateTime:   dateToTimestamp(startDate),
          endDateTime:     dateToTimestamp(endDate),
          closingDateTime: dateToTimestamp(closingDate),
        };
        await courseService.addDate(courseId, payload);
        toast.success('Date Added', 'Intake date added to course.');
      }

      setDrawerOpen(false);
      setEditingDate(null);
      await fetchDates();
      onCourseChange?.();
    } catch (e) {
      console.error(e);
      toast.error('Error', editingDate ? 'Failed to update intake date.' : 'Failed to add intake date.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDate = (d: CourseDateModelView) => {
    const label = d.periodDisplay ?? d.startDisplay ?? `Date ${d.dateID}`;
    setConfirmMessage(`Remove intake "${label}" from this course?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await courseService.removeDate(courseId, d.dateID);
        await fetchDates();
        onCourseChange?.();
        toast.warning('Date Removed', 'Intake date removed.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove intake date.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  const statusColor = (status?: string | null) => {
    switch (status) {
      case 'Open':        return 'green';
      case 'In Progress': return 'blue';
      case 'Closed':      return 'red';
      case 'Completed':   return 'gray';
      default:            return 'gray';
    }
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Date"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* Add/Edit drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={<Text fw={600} size="md">{editingDate ? 'Edit Intake Date' : 'Add Intake Date'}</Text>}
        position="right"
        size="md"
        padding="md"
      >
        <Stack gap="md">
          <Select
            label="Location"
            placeholder="Select a location"
            disabled={form.isRemote}
            data={locations.map((l) => ({
              value: String(l.locationID),
              label: l.name ?? l.locationName ?? `Location ${l.locationID}`,
            }))}
            value={form.locationID ? String(form.locationID) : null}
            onChange={(val) => setForm((f) => ({ ...f, locationID: Number(val) }))}
            searchable
            clearable
          />

          <Switch
            label="Remote / Online"
            checked={form.isRemote}
            onChange={(e) => setForm((f) => ({
              ...f,
              isRemote: e.currentTarget.checked,
              locationID: e.currentTarget.checked ? 0 : f.locationID,
            }))}
          />

          <Select
            label="Frequency"
            placeholder="Select frequency"
            data={FREQUENCY_OPTIONS}
            value={form.frequency ?? null}
            onChange={(val) => setForm((f) => ({ ...f, frequency: val }))}
            clearable
          />

          <DateTimePicker
            label="Start Date"
            placeholder="Pick start date & time"
            required
            value={startDate}
            onChange={setStartDate}
          />

          <DateTimePicker
            label="End Date"
            placeholder="Pick end date & time"
            required
            value={endDate}
            onChange={setEndDate}
          />

          <DateTimePicker
            label="Registration Closing Date"
            placeholder="Pick closing date & time"
            required
            value={closingDate}
            onChange={setClosingDate}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSaveDate}>
              {editingDate ? 'Save Changes' : 'Add Date'}
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <Group justify="flex-end" mb="md">
         <Tooltip label="Refresh" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={() => fetchDates()}
              loading={loading}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        <Button leftSection={<IconPlus size={16} />} onClick={openDrawer}>
          Add Date
        </Button>
      </Group>

      <Divider mb="md" />

      {dates.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No intake dates added yet.</Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Period</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Frequency</Table.Th>
              <Table.Th>Closing</Table.Th>
              <Table.Th>Mode</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dates.map((d) => (
              <Table.Tr key={d.dateID}>
                <Table.Td>{d.periodDisplay ?? d.startDisplay ?? '—'}</Table.Td>
                <Table.Td>{d.locationName ?? '—'}</Table.Td>
                <Table.Td>{d.frequency ?? '—'}</Table.Td>
                <Table.Td>{d.closingDisplay ?? '—'}</Table.Td>
                <Table.Td>{d.modeDisplay ?? (d.isRemote ? 'Remote' : '—')}</Table.Td>
                <Table.Td>
                  <Badge
                    color={statusColor(d.intakeWindowStatus)}
                    variant="light"
                    style={
                      d.intakeWindowStatusColor
                        ? { backgroundColor: `${d.intakeWindowStatusColor}22`, color: d.intakeWindowStatusColor }
                        : undefined
                    }
                  >
                    {d.intakeWindowStatus ?? (d.isActive ? 'Active' : 'Inactive')}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" onClick={() => openEditDrawer(d)}>
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" onClick={() => handleRemoveDate(d)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </>
  );
}